import type { ConcentrationRow } from "../../utils/portfolio-risk-concentration/api/types";

/**
 * Formatters and row-ordering shared by Monitor, Capacity and Joints.
 *
 * These were copy-pasted verbatim across MonitorPage and CapacityPage; Joints would
 * have been the fourth copy. The reason to extract is not tidiness, it is
 * `pctDistance` below: distance-to-threshold divides by `thresholdPct` in four places
 * across the two pages, and a missing guard there shows up as a silent `NaN` in a
 * risk table. One definition means one place to read the guard.
 */

/**
 * A percentage as text, null-safe.
 *
 * Null-safe because a row can be UNMEASURED — `currentDollarPct` is null when the limit's
 * rule cannot be evaluated. Renders an em dash, matching `thr`, so an empty cell reads as
 * "no figure" rather than as 0.00%.
 */
export const pct = (v: number | null | undefined) =>
  v === null || v === undefined ? "—" : `${v.toFixed(2)}%`;

/**
 * A THRESHOLD as text, always to two decimals, null-safe.
 *
 * Thresholds are stored as fractions and multiplied by 100 on the way out, so they arrive
 * carrying IEEE-754 noise — 0.035 * 100 is 3.5000000000000004, and Manage limits rendered
 * exactly that. The backend now rounds at the API boundary too (both halves matter: the
 * round stops the artifact reaching anything at all, this stops the next raw interpolation
 * reintroducing it), and every threshold on every page goes through here so the column
 * reads consistently against the Current percentage beside it, which already used `pct`.
 */
export const thr = (v: number | null | undefined) =>
  v === null || v === undefined ? "—" : `${v.toFixed(2)}%`;

/**
 * An ADVANCE RATE as text — "85%", not "85.00%".
 *
 * Separate from `thr` rather than reusing it, and the difference is not cosmetic. A
 * threshold is a fraction that gets ×100'd on the way out and arrives carrying IEEE-754
 * noise, so it is always pinned to two decimals. An advance rate is entered as a percent and
 * stored as one — 85 means 85% — so two forced decimals would render a round negotiated rate
 * as "85.00%" and imply a precision the term sheet does not have.
 *
 * Null-safe like everything here, and the null case matters more than usual: a null advance
 * rate means NO RATE RECORDED, which is not 100%. An em dash says "unknown"; anything else
 * would assert a haircut nobody agreed.
 */
export const ratePct = (v: number | null | undefined) =>
  v === null || v === undefined
    ? "—"
    : `${v.toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;

/**
 * ── Every money formatter below is NULL-SAFE, and that is load-bearing ──────────────
 *
 * An UNMEASURED row (a limit whose rule cannot be evaluated — see RagStatus) carries null
 * in currentDollars, headroomDollars and friends. Two different failures follow if these
 * are not guarded, and the quieter one is the dangerous one:
 *
 *   usd()  dereferences  ->  `null.toLocaleString` THROWS. React unmounts the tree and the
 *          whole table goes blank. Loud, and it is what actually happened once.
 *   usdM() does arithmetic first  ->  `null / 1e6` is 0, so it renders "$0.0M". No error
 *          anywhere. A limit with unknown exposure reads as having zero exposure and a
 *          headroom of zero. That is a fabricated number on a risk page.
 *
 * So the guard is not defensive tidiness, it is the difference between "—" and a made-up
 * figure. `strict` is false in tsconfig.json, so strictNullChecks is OFF and the compiler
 * will not flag either case: these guards are the only thing standing between a null and
 * the screen. Keep them on every formatter, including any added later.
 */
export const usd = (v: number | null | undefined) =>
  v === null || v === undefined
    ? "—"
    : v.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

export const usdM = (v: number | null | undefined) =>
  v === null || v === undefined ? "—" : `$${(v / 1_000_000).toFixed(1)}M`;

/** Signed, so a breach reads as a run-off requirement rather than as capacity. */
export const usdMSigned = (v: number | null | undefined) =>
  v === null || v === undefined
    ? "—"
    : `${v < 0 ? "-" : ""}$${(Math.abs(v) / 1_000_000).toFixed(1)}M`;

/**
 * Signed, and to the dollar rather than rounded to millions.
 *
 * For the facility cards, where "$833.8M" hides which side of a boundary a figure sits on
 * and cannot be reconciled against anything. Intl's currency formatter already emits the
 * minus sign, but it places it inside the symbol ("-$49,322,538" vs "$-49,322,538")
 * inconsistently across locales, so the sign is applied here and the magnitude formatted
 * unsigned.
 */
export const usdSigned = (v: number | null | undefined) =>
  v === null || v === undefined ? "—" : `${v < 0 ? "-" : ""}${usd(Math.abs(v))}`;

/**
 * Has this limit already gone past what it is allowed?
 *
 * Two tests OR'd, because a limit can breach either constraint. `headroomDollars < 0` is
 * the general answer — headroom is signed precisely so a breach reads as the run-off
 * required to cure it — and the percentage comparison catches the boundary case where
 * headroom rounds to exactly zero.
 *
 * NOT the RED badge: RED starts at 5% distance from the threshold, so a limit can be RED
 * and still compliant. Reading breach off the status would label a limit at 96% of its
 * threshold as "Breached", which is the opposite error to the one this fixes.
 */
export function isBreached(
  r: Pick<ConcentrationRow, "currentDollarPct" | "thresholdPct" | "headroomDollars">,
): boolean {
  // An unmeasured limit is neither breached nor compliant — it is unknown. False is the only
  // safe answer here, and it is why the row carries NOT_MEASURED rather than a colour: the
  // badge states what this predicate cannot.
  if (r.currentDollarPct === null || r.headroomDollars === null) return false;
  return r.headroomDollars < 0 || r.currentDollarPct > r.thresholdPct;
}

/**
 * What the Direction column says.
 *
 * Direction is derived from the last two points of the trend, so it describes MOVEMENT and
 * knows nothing about level. On a breached limit that produced sentences that were true and
 * useless: "No FICO / 0 FICO — 61.84% against a 5% threshold — Approaching". It is not
 * approaching anything; it went through it long ago. Two breached limits could also read
 * "Approaching" and "Receding", which looks like they are in different states when both are
 * simply over.
 *
 * So a breach takes over the column. The movement is not lost — the Trend sparkline sits
 * immediately to the left and is what actually shows it — and for a limit that is already
 * over, "how far past" matters more than "which way it drifted last month", which is what
 * Headroom beside it reports.
 */
export function directionLabel(
  r: Pick<ConcentrationRow, "currentDollarPct" | "thresholdPct" | "headroomDollars" | "direction">,
): string {
  // An unmeasured limit has no trend to have a direction. The API sends "STABLE" as the
  // least-assertive placeholder, but rendering "Stable" would state that the exposure is
  // holding steady — a claim about a number we do not have. Em dash, like every other cell
  // on the row.
  if (r.currentDollarPct === null) return "—";
  return isBreached(r) ? "Breached" : DIRECTION_LABEL[r.direction];
}

export const DIRECTION_LABEL: Record<ConcentrationRow["direction"], string> = {
  APPROACHING: "Approaching",
  RECEDING: "Receding",
  STABLE: "Stable",
};

/**
 * Fractional distance to a limit's threshold: 0 means at the threshold, 1 means
 * nothing drawn.
 *
 * Returns 1 when there is no positive threshold, so a threshold-less row sorts LAST
 * (unconstrained) rather than first. Joint rows always carry thresholdPct = 100, so
 * this guard is for defence against live data, not for them.
 */
export function pctDistance(r: Pick<ConcentrationRow, "thresholdPct" | "currentDollarPct">): number {
  // 1 for an unmeasured row — the same answer a threshold-less row gets, so it sorts LAST
  // rather than presenting as the tightest thing on the page.
  if (r.currentDollarPct === null) return 1;
  return r.thresholdPct > 0 ? (r.thresholdPct - r.currentDollarPct) / r.thresholdPct : 1;
}

/**
 * The limits a banner names, as one string.
 *
 * Exists because the Monitor and Capacity banners both counted one set and listed another:
 * the title said `rows.filter(status === "RED").length` while the description listed every
 * non-GREEN row. On the external book that read "4 limit(s) at or near breach" above a list
 * of seven — three of which were merely amber. A banner whose own two halves disagree is
 * worse than either half alone, because there is no way to tell which one is the answer.
 *
 * Duplicates are disambiguated by BOOK rather than collapsed. The same limit description
 * exists in both external books, so the old list showed "No FICO / 0 FICO" twice with
 * nothing to distinguish them, reading as a rendering bug. Deduping would have been the
 * wrong fix — they are two different limits, on two different books, both in trouble — so
 * the book is appended, and ONLY where a name would otherwise repeat, so the common case
 * stays uncluttered.
 */
export function limitNames(
  rows: Pick<ConcentrationRow, "limitDesc" | "facilityCode">[],
  bookLabel: Record<string, string> = {},
): string {
  const seen = new Map<string, number>();
  for (const r of rows) seen.set(r.limitDesc, (seen.get(r.limitDesc) ?? 0) + 1);
  return rows
    .map((r) =>
      (seen.get(r.limitDesc) ?? 0) > 1
        ? `${r.limitDesc} (${bookLabel[r.facilityCode] ?? r.facilityCode})`
        : r.limitDesc,
    )
    .join(", ");
}

/**
 * RED, AMBER, GREEN, then NOT_MEASURED.
 *
 * Unmeasured sorts BELOW green, not above red. It is a gap in our coverage, not a portfolio
 * emergency — pinning it to the top of a risk table every day would train people to ignore
 * the top of the table. It stays visible by being on the page at all, which it was not before.
 */
export function severityRank(s: ConcentrationRow["status"]): number {
  return s === "RED" ? 0 : s === "AMBER" ? 1 : s === "GREEN" ? 2 : 3;
}

/** Worst status first, then closest to its own threshold. */
export function sortBySeverity<T extends ConcentrationRow>(rows: T[]): T[] {
  return [...rows].sort((a, b) => {
    const r = severityRank(a.status) - severityRank(b.status);
    if (r !== 0) return r;
    return pctDistance(a) - pctDistance(b);
  });
}
